import { useState } from "react";
import { useParams } from "@nodes/react-router";
import { CiUnlock } from "react-icons/ci";
import {
  Container,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  Box,
  CircularProgress,
  Snackbar,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
// import Loading from "@shared/components/Loading";
import { UseCustomerExceptions, UseCustomerActiveException, UseCustomerBalanceV2 } from "@shared/server/fetcher";
import backend from "@shared/server/backend";
import { exceedsMonthlyForUnblock } from "@shared/functions/customerBlock";

const MAX_EXCEPTIONS = 3;

const CustomerUnBlock = () => {
  const { id: customer_id } = useParams();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  // mainOnly=1 on purpose: that is the key the page guards and the Drawer banner read,
  // so this is the cache that has to be refreshed after a self-unblock. Called with the
  // default (0) it revalidated a different key and the user stayed blocked.
  const {
    data: customer,
    isLoading: isLoading3,
    mutate: mutate3,
  } = UseCustomerBalanceV2(customer_id, 1);
  const { data: exceptions, isLoading: isLoading1, mutate: mutate1 } = UseCustomerExceptions(customer_id);
  const { data: activeException, isLoading: isLoading2, mutate: mutate2 } = UseCustomerActiveException(customer_id);

  const handleCreateException = async () => {
    setSubmitting(true);
    try {
      await backend.post(`/customers/${customer_id}/exceptions`, { customer_id });
      await mutate1();
      await mutate2();
      await mutate3();
      setSuccessMessage("Cuenta desblocqueada hasta mañana!");
    } catch (err) {
      setError("Hubo un error desblocqueando cuenta.");
    } finally {
      setSubmitting(false);
    }
  };

  const exceptionCount = exceptions?.length;
  const isLimitReached = exceptionCount >= MAX_EXCEPTIONS;
  // Owing more than one month's payment means prior unpaid debt, which has to be paid down
  // rather than deferred again. Surfaced as its own alert below instead of only disabling
  // the button, so the customer is told why.
  const isOverBalance = exceedsMonthlyForUnblock(customer);
  const isButtonDisabled =
    isLimitReached || isOverBalance || submitting || activeException?.length > 0;
  return (
    <Container maxWidth="sm">
      <Box my={4}>
        <Typography variant="h6" gutterBottom>
          Desblocquear Cuenta
        </Typography>

        <Typography variant="body1" paragraph>
          Puede desblocquear su cuenta por falta de pagos temporalmente.
        </Typography>

        <Typography variant="body1" paragraph>
          Un maximo de {MAX_EXCEPTIONS} es permitido por mes.
        </Typography>

        {activeException?.length > 0 && (
          <Box padding={2}>
            <Alert severity="success">Su cuenta ha sido desblocqueada.</Alert>
          </Box>
        )}

        {isLoading1 || isLoading2 || isLoading3 ? (
          <Box display="flex" justifyContent="center" my={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper elevation={3} style={{ padding: "1rem" }}>
            <Typography variant="subtitle1">
              Haz usado {exceptionCount} de {MAX_EXCEPTIONS} desblocqueos este mes.
            </Typography>

            <List dense>
              {exceptions.map((ex, idx) => (
                <ListItem key={idx}>
                  <ListItemText primary={`${idx + 1}. Desblocqueo en ${new Date(ex.created_at).toLocaleString()}`} />
                </ListItem>
              ))}
            </List>

            {isLimitReached && (
              <Box mt={2}>
                <Alert severity="warning">Haz usado el maximo limite permitido de este mes.</Alert>
              </Box>
            )}

            {isOverBalance && (
              <Box mt={2}>
                <Alert severity="warning">
                  Su balance excede el pago mensual. Debe reducir su balance para poder desblocquear su cuenta.
                </Alert>
              </Box>
            )}

            <Box mt={3} display="flex" justifyContent="center">
              <Button
                startIcon={<CiUnlock size="1em" />}
                variant="contained"
                color="primary"
                onClick={handleCreateException}
                disabled={isButtonDisabled}
              >
                {submitting ? "Procesando..." : "Desblocquear Cuenta"}
              </Button>
            </Box>
          </Paper>
        )}

        <Snackbar open={!!successMessage} autoHideDuration={3000} onClose={() => setSuccessMessage("")}>
          <Alert onClose={() => setSuccessMessage("")} severity="success">
            {successMessage}
          </Alert>
        </Snackbar>

        <Snackbar open={!!error} autoHideDuration={3000} onClose={() => setError(null)}>
          <Alert onClose={() => setError(null)} severity="error">
            {error}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default CustomerUnBlock;
